import { createContext, ReactNode, useCallback, useEffect, useReducer } from "react";
import { isValidToken, setSession } from "../utils/jwt";
import LoadingLogo from "../components/LoadingLogo";

interface State {
  isAuthenticated: boolean;
  isInitialized: boolean;
  token: string | null;
}

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  token: null,
};

enum Types {
  init,
  logout,
  authenticate,
}

interface Action<T = any> {
  type: Types;
  payload: T;
}

function reducer(state: State, { type, payload }: Action): State {
  switch (type) {
    case Types.init:
      return {
        ...state,
        isInitialized: true,
        isAuthenticated: payload.isAuthenticated,
        token: payload.token,
      };
    case Types.authenticate:
      return {
        ...state,
        isAuthenticated: true,
        token: payload.token,
      };
    case Types.logout:
      return {
        ...state,
        isAuthenticated: false,
        token: null,
      };
    default:
      throw new Error("Invalid action type was provided");
  }
}

interface Context extends State {
  onAuth: (token: string) => void;
  onInit: (token: string | null) => void;
  onLogout: () => void;
}

export const AuthContext = createContext<Context>({
  ...initialState,
  // default value for functions
  onInit: () => {},
  onAuth: () => {},
  onLogout: () => {},
});

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // initialize auth
  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken && isValidToken(accessToken)) {
      setSession(accessToken);
      onInit(accessToken);
    } else onInit(null);
  }, []); // eslint-disable-line

  const onAuth = useCallback((token: string) => {
    setSession(token);
    dispatch({
      type: Types.authenticate,
      payload: { token },
    });
  }, []);

  const onInit = useCallback((token: string | null) => {
    dispatch({
      type: Types.init,
      payload: { isAuthenticated: !!token, token },
    });
  }, []);

  const onLogout = useCallback(() => {
    setSession(null);
    dispatch({ type: Types.logout, payload: null });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        onInit,
        onAuth,
        onLogout,
      }}
    >
      {state.isInitialized ? children : <LoadingLogo />}
    </AuthContext.Provider>
  );
};
